import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BrandMark } from './ui/BrandMark';
import { ThemeToggle } from './ui/ThemeToggle';
import { LanguageToggle } from './ui/LanguageToggle';
import { useLanguage } from '../contexts/LanguageContext';

interface HeaderProps {
  title: string;
}

type ChipTone = 'error' | 'warning' | 'neutral';

interface SubscriptionChip {
  label: string;
  value: string;
  tone: ChipTone;
}

const DAY_MS = 1000 * 60 * 60 * 24;
/** Дальше месяца срок подписки — не новость, чип не показываем. */
const NOTICE_WINDOW_DAYS = 30;
/** Ближе недели — уже срочно, чип становится предупреждающим. */
const URGENT_DAYS = 7;

export const Header: React.FC<HeaderProps> = ({ title }) => {
  const { partner } = useAuth();
  const { t } = useLanguage();

  const describeSubscription = (daysLeft: number): SubscriptionChip => {
    if (daysLeft < 0) {
      const past = Math.abs(daysLeft);
      return {
        label: t.cabinet.subAccessClosed,
        value: past === 0 ? t.common.today : `${past} ${t.common.daysAgo}`,
        tone: 'error',
      };
    }
    if (daysLeft === 0) return { label: t.cabinet.subClosesToday, value: t.common.today, tone: 'warning' };
    if (daysLeft === 1) return { label: t.cabinet.subClosesTomorrow, value: t.common.tomorrow, tone: 'warning' };
    return {
      label: t.cabinet.subClosesIn,
      value: `${daysLeft} ${t.common.day}`,
      tone: daysLeft <= URGENT_DAYS ? 'warning' : 'neutral',
    };
  };

  const renderSubscriptionChip = () => {
    const sub = partner?.subscription;
    if (!partner) return null;
    // Неактивированная подписка уже объяснена плашкой-нотисом выше — дублировать нечем.
    if (sub?.is_activated === false) return null;

    const expiresAt = sub?.expires_at ?? partner.expires_at ?? null;
    const daysLeft =
      sub?.days_left ??
      (expiresAt ? Math.floor((new Date(expiresAt).getTime() - Date.now()) / DAY_MS) : null);

    if (daysLeft === null || daysLeft > NOTICE_WINDOW_DAYS) return null;

    const chip = describeSubscription(daysLeft);
    return (
      <Link
        to="/billing"
        className={`m3-sub-chip m3-sub-chip--${chip.tone}`}
        aria-label={`${chip.label}: ${chip.value}. ${t.cabinet.subOpenBilling}`}
      >
        <span className="m3-sub-chip-label">{chip.label}</span>
        <span className="m3-sub-chip-value">{chip.value}</span>
      </Link>
    );
  };

  return (
    <header className="m3-topbar">
      <Link to="/" className="m3-brand" aria-label="QPrice — на главную">
        <BrandMark />
      </Link>
      <div className="m3-topbar-title">{title}</div>
      <div className="m3-topbar-actions">
        {renderSubscriptionChip()}
        <LanguageToggle />
        <ThemeToggle />
      </div>
    </header>
  );
};
